/**
 * The ONE reading of a broker account's status.
 *
 * WHY THIS EXISTS
 * `account.status` is a plain String column that conflates TWO unrelated ideas and
 * carries values from THREE vocabularies:
 *   - backend schema default (schemas/broker_account.py:24) ....... "pending"
 *   - backend schema validator (:33) allows ONLY ................... active | inactive | suspended
 *     (so the validator rejects its own default — it never fires on defaults)
 *   - the verify endpoint (endpoints/broker_accounts.py:594) writes  "verified"
 *     (also absent from that validator's list)
 *
 * Because verification ("is it verified?") and enablement ("is it switched on?")
 * share one field, a value of "verified" tells you nothing about whether the account
 * is enabled, and "active" tells you nothing about whether it is verified. The UI
 * therefore CANNOT honestly show both as separate facts. It shows the one fact the
 * field actually carries, spelled out.
 *
 * This module exists because the list and the detail page used to read the same field
 * two different ways:
 *   - list    (TradeAccounts):  isVerified = status === "verified"  -> else "Inactive"
 *   - details (AccountDetails): status.toUpperCase()                -> "ACTIVE"
 * so ONE account rendered "Inactive" on the list and "ACTIVE" on its own detail page.
 * Both now call in here.
 *
 * TODO(backend): split `status` into `is_verified` + `is_enabled` (and fix the schema
 * default that its own validator rejects). Only that can express all three of
 * verified / enabled / session as independent facts.
 */

// Raw `status` -> a self-describing label + a DS badge variant.
// The label is a STATEMENT ("Pending verification"), never a bare adjective
// ("ACTIVE") that leaves the reader to guess which axis it refers to.
const ACCOUNT_STATUS = {
  // "verified" is the ONLY value that means ready-to-trade, so it is the only one
  // that gets the success colour.
  verified:  { label: "Verified",             variant: "profit"  },

  // "active" is NOT a success state, despite the word. Because verification and
  // enablement share this one field, an account that had been verified would READ
  // "verified" — so "active" necessarily means enabled-but-not-verified. Colouring
  // it green put a reassuring badge directly above the card's own red "Account Not
  // Verified" row, which is the same mixed message we set out to remove. Neutral.
  active:    { label: "Active",               variant: "neutral" },

  pending:   { label: "Pending verification", variant: "warn"    },
  inactive:  { label: "Disabled",             variant: "neutral" },
  suspended: { label: "Suspended",            variant: "loss"    },
};

/**
 * Paper trading has no broker to log into. The papertrade adapter genuinely has no
 * /api/v1/login-url endpoint — it 404s BY DESIGN, not because anything is down — so
 * a paper account's "Connect" button could never succeed, and failed by dumping an
 * internal hostname and port at the user.
 *
 * `broker_is_paper` is already on BrokerAccountResponse (schemas/broker_account.py:92),
 * already sent by the API, and already honoured by SmartTrade and StrategyBuilder.
 * This feature was the only one ignoring it.
 */
export const isPaperAccount = (account) => account?.broker_is_paper === true;

export const accountStatusOf = (account) => {
  // A paper account is active by definition: there is no broker to verify against and
  // no credentials to be wrong. Reporting it as "Pending verification" was describing
  // a step that does not exist for it.
  if (isPaperAccount(account)) {
    return { label: "Paper trading", variant: "accent" };
  }

  const key = account?.status?.toLowerCase();
  return (
    ACCOUNT_STATUS[key] || {
      // Don't invent a meaning for a value we don't know — say so.
      label: account?.status ? `Status: ${account.status}` : "Status unknown",
      variant: "neutral",
    }
  );
};

/**
 * The session — the axis that actually changes daily, since a broker session must be
 * re-established each market day.
 *
 * DO NOT TRUST `account.session_status`. It reads "active" whenever a session ROW
 * EXISTS, not when that session is still valid:
 *
 *   services/broker_account.py:
 *     is_active = account_id in active_session_data   # <- a row exists
 *     "session_status": "active" if is_active else "inactive"
 *
 * Nothing flips it back when the session lapses, so an expired session still reports
 * "active". That produced a card reading "Session active" and "Expired" at the same
 * time — two facts read off one row: its existence and its expiry.
 *
 * `session_expires_at` is the only truthful signal, so liveness is derived from the
 * clock. session_status is deliberately ignored.
 */
export const sessionStatusOf = (account) => {
  // Paper is always connected: there is no broker session to establish, expire, or
  // re-establish each market day. `needsLogin: false` is what suppresses the Connect
  // button and the login modal for these accounts.
  if (isPaperAccount(account)) {
    return {
      isActive: true,
      hasSession: true,
      needsLogin: false,
      label: "Always connected",
      variant: "profit",
      // No countdown: nothing expires, so a timer would be counting down to nothing.
      expiresAt: null,
    };
  }

  const raw = account?.session_expires_at || null;
  const expiresAtMs = raw ? new Date(raw).getTime() : NaN;
  const hasSession = Number.isFinite(expiresAtMs);
  const isActive = hasSession && expiresAtMs > Date.now();

  return {
    isActive,
    hasSession,
    needsLogin: true,
    // "Expired" is a different statement from "never connected" — a lapsed session
    // means you were connected today and need to reconnect; no session at all means
    // this account has never been linked.
    label: isActive
      ? "Session active"
      : hasSession
      ? "Session expired"
      : "No active session",
    variant: isActive ? "profit" : hasSession ? "warn" : "neutral",
    // Only meaningful while live; the countdown is rendered by SessionTimer. When the
    // session has lapsed the pill already says so, so the timer is not shown again.
    expiresAt: isActive ? raw : null,
  };
};

export const isAccountVerified = (account) =>
  account?.status?.toLowerCase() === "verified";
